import type { DataTableColumn, DataTableColumnGroup, DataTableRow } from '@ui/chart/DataTable';
import type { BarChartSeries } from '@ui/chart/BarChart';
import {
	GuestMixEvolution, GuestMixFigures, GuestMixPart, GuestMixReport, GuestMixSplit, RevenueScope, SITUATION_DAYS, SituationDays, VatBasis,
} from '@app/model/bi/Bi';
import { occupiedOf, revenueOf } from '../Bi.utilities';

// The figures of the Guest Mix report (BI report 4, ROO2-264). The formulas are those of the Guest Mix brief (chapter 4.6)
// with the grill decisions of 2026-10-21: every figure of a row of several properties or of the total is a sum of its
// parts, and every ratio comes from those sums, never from an average.
// MIX % is the share of the room nights of all rows, Unknown included; a property line divides by the room nights of its
// own property.

// The blocks of the Columns menu. TODAY always shows.
export type ColumnSet = 'ly' | 'd1' | 'd3' | 'd7' | 'd14' | 'd30' | 'pu1' | 'pu3' | 'pu7' | 'pu14' | 'pu30';
export type EvolutionMeasure = 'rn' | 'rev' | 'adr';

// The split of the page and its value in the address (?split=, decision 18).
export const SPLITS: { value: GuestMixSplit, label: string, param: string, noun: string }[] = [
	{ value: 'GUEST_TYPE', label: 'Guest type', param: 'guest-type', noun: 'guest types' },
	{ value: 'NATIONALITY', label: 'Nationality', param: 'nationality', noun: 'nationalities' },
	{ value: 'LENGTH_OF_STAY', label: 'Length of stay', param: 'length-of-stay', noun: 'lengths of stay' },
	{ value: 'LEAD_TIME', label: 'Lead time', param: 'lead-time', noun: 'lead times' },
];
export const splitOf = (param: string | null): GuestMixSplit => SPLITS.find(s => s.param === param)?.value ?? 'GUEST_TYPE';
export const splitInfo = (split: GuestMixSplit) => SPLITS.find(s => s.value === split) ?? SPLITS[0];

// The keys of the fixed splits in their order (decision 13), and the words of the keys (decisions 3, 12, 13).
const FIXED: Partial<Record<GuestMixSplit, string[]>> = {
	LENGTH_OF_STAY: ['N1', 'N2', 'N3_4', 'N5_7', 'N8_PLUS'],
	LEAD_TIME: ['SAME_DAY', 'D1_3', 'D4_7', 'D8_30', 'M1_3', 'M3_PLUS', 'UNKNOWN'],
};
const LABELS: Record<string, string> = {
	SOLO: 'Solo', COUPLE: 'Couple', FAMILY: 'Family', ADULTS_3_PLUS: '3+ adults', UNKNOWN: 'Unknown',
	N1: '1 night', N2: '2 nights', N3_4: '3-4 nights', N5_7: '5-7 nights', N8_PLUS: '8+ nights',
	SAME_DAY: 'Same day', D1_3: '1-3 days', D4_7: '4-7 days', D8_30: '8-30 days', M1_3: '1-3 months', M3_PLUS: '3+ months',
};
export const isFixed = (split: GuestMixSplit) => FIXED[split] !== undefined;
export const isUnknown = (split: GuestMixSplit, key: string) => split === 'NATIONALITY' ? key === '' : key === 'UNKNOWN';
// ROO2-272 D2: a booked room that 5stelle sent only as a cancellation has no guest data. It has its own row, the last one, in
// the Guest type and the Nationality splits. Like Unknown: no Δ%, not in Biggest gain / loss, no build-up tile.
export const CANCELLED_NO_DATA = 'CANCELLED_NO_DATA';
// A row that measures missing data: Unknown or Cancelled, no guest data.
export const isNoData = (split: GuestMixSplit, key: string) => isUnknown(split, key) || key === CANCELLED_NO_DATA;
// Decision 4: the English country name of the ISO-2 code; a code that the browser does not know shows as it is.
const regions = typeof Intl.DisplayNames === 'function' ? new Intl.DisplayNames(['en'], { type: 'region' }) : null;
export const labelOf = (split: GuestMixSplit, key: string): string => {
	if (key === CANCELLED_NO_DATA) return 'Cancelled, no guest data';
	if (isUnknown(split, key)) return 'Unknown';
	if (split === 'NATIONALITY') {
		try { return regions?.of(key) ?? key; } catch { return key; }
	}
	return LABELS[key] ?? key;
};

// The first rows of a list (decision 4), the bars of the mix (decision 19) and the lines of the evolution (decision 20).
export const ROW_LIMIT = 15;
export const MIX_BARS = 8;
export const EVOLUTION_LINES = 6;
const DAYS: SituationDays[] = [1, 3, 7, 14, 30];

const ZERO: GuestMixFigures = { roomNights: 0, occupiedRooms: 0, roomRevenue: 0, roomRevenueNet: 0, totalRevenue: 0, totalRevenueNet: 0 };
const cents = (v: number) => Math.round(v * 100) / 100;
const plus = (a: number | null, b: number | null): number | null => a === null || b === null ? null : cents(a + b);
// A sum of figures: a revenue is null when one of its parts is null (never a partial sum, ROO2-236).
export const add = (a: GuestMixFigures, b: GuestMixFigures): GuestMixFigures => ({
	roomNights: a.roomNights + b.roomNights, occupiedRooms: occupiedOf(a) + occupiedOf(b), roomRevenue: plus(a.roomRevenue, b.roomRevenue), roomRevenueNet: plus(a.roomRevenueNet, b.roomRevenueNet),
	totalRevenue: cents(a.totalRevenue + b.totalRevenue), totalRevenueNet: plus(a.totalRevenueNet, b.totalRevenueNet),
});

// The sums of a row: today and the situations D-1 … D-30 (SITUATION_DAYS), STLY, LY actual, and today's figures of the
// properties that have stays in the last-year period (like for like: the base of Δ% and MIX Δ).
export type Sums = { cuts: GuestMixFigures[], stly: GuestMixFigures, ly: GuestMixFigures, comparable: GuestMixFigures };
const emptySums = (): Sums => ({ cuts: SITUATION_DAYS.map(() => ZERO), stly: ZERO, ly: ZERO, comparable: ZERO });
const addPart = (s: Sums, p: GuestMixPart, missing: Set<number>): Sums => ({
	cuts: SITUATION_DAYS.map((d, i) => add(s.cuts[i], p.situations.find(x => x.daysBefore === d) ?? ZERO)),
	stly: add(s.stly, p.stly), ly: add(s.ly, p.lyActual),
	comparable: missing.has(p.companyHotelId) ? s.comparable : add(s.comparable, p.situations.find(x => x.daysBefore === 0) ?? ZERO),
});
export const sumOf = (parts: GuestMixPart[], missing: Set<number>): Sums => parts.reduce((s, p) => addPart(s, p, missing), emptySums());

export type GuestMixNode = {
	id: string,
	// The key of the split; a property line: the key of its row.
	key: string,
	kind: 'VALUE' | 'PROPERTY',
	title: string,
	// A value of all properties: the properties with room nights today.
	count: number,
	unknown: boolean,
	// A property with no stays in the last-year period: its last-year columns have no value.
	noLastYear: boolean,
	sums: Sums,
	// The sums that MIX % divides by: all rows, or the property's own rows for a property line.
	base: Sums,
	children: GuestMixNode[],
};

export type GuestMixTable = {
	split: GuestMixSplit,
	// The rows: the fixed order of the split, or the order of the caller (the table sorts by RN today).
	rows: GuestMixNode[],
	total: Sums,
};

// Room nights today, largest first; on a tie, by name.
const byRoomNights = (a: GuestMixNode, b: GuestMixNode): number => {
	const diff = b.sums.cuts[0].roomNights - a.sums.cuts[0].roomNights;
	return diff !== 0 ? diff : a.title.localeCompare(b.title);
};

// The table of the split: one row per key, and (withProperties: All properties) its property lines.
export const buildTable = (report: GuestMixReport, nameOf: (companyHotelId: number) => string, withProperties: boolean): GuestMixTable => {
	const missing = new Set(report.lastYearMissing);
	const total = sumOf(report.parts, missing);
	const byHotel = new Map<number, GuestMixPart[]>();
	report.parts.forEach(p => byHotel.set(p.companyHotelId, [...(byHotel.get(p.companyHotelId) ?? []), p]));
	const hotelTotals = new Map([...byHotel.entries()].map(([id, ps]) => [id, sumOf(ps, missing)]));
	const byKey = new Map<string, GuestMixPart[]>();
	report.parts.forEach(p => byKey.set(p.key, [...(byKey.get(p.key) ?? []), p]));
	const rows = [...byKey.entries()].map(([key, parts]): GuestMixNode => {
		const children = withProperties ? parts.map((p): GuestMixNode => ({
			id: `${key}|${p.companyHotelId}`, key, kind: 'PROPERTY', title: nameOf(p.companyHotelId), count: 0, unknown: false,
			noLastYear: missing.has(p.companyHotelId), sums: sumOf([p], missing), base: hotelTotals.get(p.companyHotelId) ?? emptySums(), children: [],
		}))
			// Brief 4.6: a property line with no room nights today, at STLY and at LY actual stays out.
			.filter(c => c.sums.cuts[0].roomNights > 0 || c.sums.stly.roomNights > 0 || c.sums.ly.roomNights > 0)
			.sort(byRoomNights) : [];
		return {
			id: key === '' ? '(unknown)' : key, key, kind: 'VALUE', title: labelOf(report.split, key),
			count: parts.filter(p => (p.situations.find(x => x.daysBefore === 0)?.roomNights ?? 0) > 0).length,
			unknown: isNoData(report.split, key), noLastYear: false, sums: sumOf(parts, missing), base: total, children,
		};
	});
	const fixed = FIXED[report.split];
	if (fixed !== undefined) rows.sort((a, b) => fixed.indexOf(a.key) - fixed.indexOf(b.key));
	else rows.sort(byRoomNights);
	// ROO2-272 D2: "Cancelled, no guest data" is always the last row (it has no room nights today).
	rows.sort((a, b) => (a.key === CANCELLED_NO_DATA ? 1 : 0) - (b.key === CANCELLED_NO_DATA ? 1 : 0));
	return { split: report.split, rows, total };
};

const ratio = (a: number | null, b: number | null): number | null => a === null || b === null || b === 0 ? null : a / b;

// (REV now − last year's REV) ÷ last year's REV. "new" when last year's REV is 0 and now is not; null (n/a) when both are 0
// or a revenue has no value. "now" is the revenue of the properties with last-year stays (like for like).
export const change = (now: number | null, lastYear: number | null): number | 'new' | null => {
	if (now === null || lastYear === null) return null;
	if (lastYear === 0) return now === 0 ? null : 'new';
	return (now - lastYear) / lastYear;
};

// TODAY LFL MIX % (ROO2-271 D1): the share of the room nights today of the properties with last-year stays.
export const mixLfl = (node: Pick<GuestMixNode, 'sums' | 'base' | 'noLastYear'>): number | null =>
	node.noLastYear ? null : ratio(node.sums.comparable.roomNights, node.base.comparable.roomNights);

// MIX Δ: the share of the room nights today (like for like) − the share at STLY, a fraction (0.081 = +8.1 pts).
export const mixDelta = (node: Pick<GuestMixNode, 'sums' | 'base' | 'noLastYear'>): number | null => {
	if (node.noLastYear) return null;
	const now = ratio(node.sums.comparable.roomNights, node.base.comparable.roomNights);
	const before = ratio(node.sums.stly.roomNights, node.base.stly.roomNights);
	return now === null || before === null ? null : now - before;
};

// The cells of a row of the table. The Δ% of an Unknown row has no value (its change measures missing data, brief 4.6).
export const cellsOf = (node: Pick<GuestMixNode, 'sums' | 'base' | 'noLastYear' | 'unknown'>, title: string, scope: RevenueScope,
	vat: VatBasis): Record<string, number | string | null> => {
	const s = node.sums;
	const rev = (f: GuestMixFigures) => revenueOf(f, scope, vat);
	const now = s.cuts[0];
	const noLy = node.noLastYear;
	const cells: Record<string, number | string | null> = {
		name: title, rn: now.roomNights, mix: ratio(now.roomNights, node.base.cuts[0].roomNights), rev: rev(now),
		adr: occupiedOf(now) === 0 ? null : ratio(rev(now), occupiedOf(now)),
		stlyRn: noLy ? null : s.stly.roomNights, stlyRev: noLy ? null : rev(s.stly),
		vsStly: noLy || node.unknown ? null : change(rev(s.comparable), rev(s.stly)),
		// Today like-for-like (ROO2-271 D1): today without the properties that have no stays in the last-year period, the base of
		// Δ% and MIX Δ. It shows when the scope has such a property (columnsOf, lfl).
		lflRn: noLy ? null : s.comparable.roomNights, lflRev: noLy ? null : rev(s.comparable),
		lflMix: mixLfl(node), stlyMix: noLy ? null : ratio(s.stly.roomNights, node.base.stly.roomNights), mixDelta: mixDelta(node),
		lyRn: noLy ? null : s.ly.roomNights, lyRev: noLy ? null : rev(s.ly),
		vsLy: noLy || node.unknown ? null : change(rev(s.comparable), rev(s.ly)),
	};
	const adrNow = occupiedOf(now) === 0 ? null : ratio(rev(now), occupiedOf(now));
	DAYS.forEach(d => {
		const i = SITUATION_DAYS.indexOf(d);
		const past = s.cuts[i];
		const pastRev = rev(past), nowRev = rev(now);
		const puRn = now.roomNights - past.roomNights;
		const puRev = nowRev === null || pastRev === null ? null : cents(nowRev - pastRev);
		const adrPast = occupiedOf(past) === 0 ? null : ratio(pastRev, occupiedOf(past));
		cells[`d${d}Rn`] = past.roomNights;
		cells[`d${d}Mix`] = ratio(past.roomNights, node.base.cuts[i].roomNights);
		cells[`d${d}Rev`] = pastRev;
		cells[`pu${d}Rn`] = puRn;
		cells[`pu${d}Rev`] = puRev;
		// ADR Δ (ROO2-271 D3): ADR today − ADR at situation D-n; no value when one of the two has no room nights. It replaces
		// PU ADR (PU REV ÷ PU RN), which was negative or very high when nights were cancelled.
		cells[`pu${d}Adr`] = adrNow === null || adrPast === null ? null : cents(adrNow - adrPast);
	});
	return cells;
};

// The columns and their header blocks, in the order of the brief (4.6). lfl (ROO2-271 D1): the scope has a property with no
// stays in the last-year period; the previous-year block then starts with today like-for-like (RN, REV and MIX %), the
// figures that Δ% and MIX Δ compare.
export const columnsOf = (split: GuestMixSplit, sets: ColumnSet[], lfl = false): { columns: DataTableColumn[], groups: DataTableColumnGroup[] } => {
	const columns: DataTableColumn[] = [{ id: 'name', label: splitInfo(split).label },
		{ id: 'rn', label: 'RN', format: 'number' }, { id: 'mix', label: 'MIX %', format: 'percent', decimals: 1 },
		{ id: 'rev', label: 'REV', format: 'money' }, { id: 'adr', label: 'ADR', format: 'money', decimals: 2 }];
	const groups: DataTableColumnGroup[] = [{ label: '', span: 1 }, { label: 'Today', span: 4 }];
	if (sets.includes('ly')) {
		if (lfl) {
			columns.push({ id: 'lflRn', label: 'TODAY LFL RN', format: 'number' }, { id: 'lflRev', label: 'TODAY LFL REV', format: 'money' },
				{ id: 'lflMix', label: 'TODAY LFL MIX %', format: 'percent', decimals: 1 });
		}
		columns.push({ id: 'stlyRn', label: 'STLY RN', format: 'number' }, { id: 'stlyRev', label: 'STLY REV', format: 'money' },
			{ id: 'vsStly', label: 'Δ%', format: 'percent', decimals: 1, signed: true, tone: 'change' },
			{ id: 'stlyMix', label: 'STLY MIX %', format: 'percent', decimals: 1 },
			{ id: 'mixDelta', label: 'MIX Δ', format: 'points', signed: true, tone: 'change' },
			{ id: 'lyRn', label: 'LY ACTUAL RN', format: 'number' }, { id: 'lyRev', label: 'LY ACTUAL REV', format: 'money' },
			{ id: 'vsLy', label: 'Δ%', format: 'percent', decimals: 1, signed: true, tone: 'change' });
		groups.push({ label: lfl ? 'Previous year · like-for-like' : 'Previous year', span: lfl ? 11 : 8 });
	}
	DAYS.filter(d => sets.includes(`d${d}` as ColumnSet)).forEach(d => {
		columns.push({ id: `d${d}Rn`, label: 'RN', format: 'number' }, { id: `d${d}Mix`, label: 'MIX %', format: 'percent', decimals: 1 },
			{ id: `d${d}Rev`, label: 'REV', format: 'money' });
		groups.push({ label: `Situation D-${d}`, span: 3 });
	});
	DAYS.filter(d => sets.includes(`pu${d}` as ColumnSet)).forEach(d => {
		columns.push({ id: `pu${d}Rn`, label: 'RN', format: 'number', signed: true, tone: 'change' },
			{ id: `pu${d}Rev`, label: 'REV', format: 'money', signed: true, tone: 'change' },
			{ id: `pu${d}Adr`, label: 'ADR Δ', format: 'money', decimals: 2, signed: true, tone: 'change' });
		groups.push({ label: `PU ${d}`, span: 3 });
	});
	return { columns, groups };
};

// The DataTable rows. A value of all properties shows its count of properties and opens to its property lines (decision 7).
export const tableRows = (table: GuestMixTable, scope: RevenueScope, vat: VatBasis): DataTableRow[] => table.rows.map(n => ({
	id: n.id, muted: n.unknown,
	cells: cellsOf(n, n.title, scope, vat),
	...(n.children.length > 0 ? {
		note: `· ${n.count}`,
		children: n.children.map(c => ({ id: c.id, cells: cellsOf({ ...c, unknown: n.unknown }, c.title, scope, vat) })),
	} : {}),
}));

// The Total: a sum of all rows; it has no MIX % and no MIX Δ (brief 4.6).
export const totalRow = (table: GuestMixTable, scope: RevenueScope, vat: VatBasis): DataTableRow => ({
	id: 'total',
	cells: { ...cellsOf({ sums: table.total, base: table.total, noLastYear: false, unknown: false }, 'Total', scope, vat), mix: null, lflMix: null, stlyMix: null, mixDelta: null,
		d1Mix: null, d3Mix: null, d7Mix: null, d14Mix: null, d30Mix: null },
});

// --- headline figures and the mix (direction B) -----------------------------------------------------------------------

// Decision 16: the row with the largest and the row with the smallest MIX Δ; Unknown stays out, and a row needs room nights
// today or at STLY. A gain must be above 0 and a loss below 0.
export const headline = (table: GuestMixTable) => {
	const candidates = table.rows.filter(n => !n.unknown && mixDelta(n) !== null && (n.sums.cuts[0].roomNights > 0 || n.sums.stly.roomNights > 0));
	const by = (sign: 1 | -1) => candidates.reduce<GuestMixNode | null>((best, n) => best === null || sign * (mixDelta(n) as number) > sign * (mixDelta(best) as number) ? n : best, null);
	const gain = by(1), loss = by(-1);
	const view = (n: GuestMixNode | null, sign: 1 | -1) => n === null || sign * (mixDelta(n) as number) <= 0 ? null
		: { title: n.title, mix: ratio(n.sums.cuts[0].roomNights, table.total.cuts[0].roomNights), points: mixDelta(n) as number };
	return { gain: view(gain, 1), loss: view(loss, -1) };
};

// The mix bars: This year and STLY, the share of the room nights of the first MIX_BARS rows of the table, and "Other (N)".
export const mixBars = (table: GuestMixTable): { categories: string[], series: BarChartSeries[] } => {
	const shown = table.rows.slice(0, MIX_BARS), rest = table.rows.slice(MIX_BARS);
	const share = (n: Sums[], at: 'now' | 'stly') => {
		const rn = n.reduce((s, x) => s + (at === 'now' ? x.cuts[0].roomNights : x.stly.roomNights), 0);
		return ratio(rn, at === 'now' ? table.total.cuts[0].roomNights : table.total.stly.roomNights);
	};
	const categories = [...shown.map(n => n.title), ...(rest.length > 0 ? [`Other (${rest.length})`] : [])];
	const values = (at: 'now' | 'stly') => [...shown.map(n => share([n.sums], at)), ...(rest.length > 0 ? [share(rest.map(n => n.sums), at)] : [])];
	return { categories, series: [
		{ id: 'thisYear', label: 'This year', role: 'own', values: values('now') },
		{ id: 'stly', label: 'STLY', role: 'compset', values: values('stly') },
	] };
};

// --- Excel export (decision 8, the export of ROO2-261) -------------------------------------------------------------------

const ALL_SETS: ColumnSet[] = ['ly', 'd1', 'd3', 'd7', 'd14', 'd30', 'pu1', 'pu3', 'pu7', 'pu14', 'pu30'];
// The column sets of the file: the ones that the user sees, in the order of the table, with TODAY first.
export const exportColumns = (sets: ColumnSet[]): string[] => ['today', ...ALL_SETS.filter(s => sets.includes(s))];

// --- evolution (ROO2-265, brief 4.3) ---------------------------------------------------------------------------------------

// One point: RN, the revenue of the switches, or ADR from the sums: revenue ÷ occupied rooms (ROO2-315 part c2; no value when
// there is no occupied room).
export const pointOf = (f: GuestMixFigures, measure: EvolutionMeasure, scope: RevenueScope, vat: VatBasis): number | null => {
	if (measure === 'rn') return f.roomNights;
	const rev = revenueOf(f, scope, vat);
	return measure === 'rev' ? rev : (occupiedOf(f) === 0 ? null : ratio(rev, occupiedOf(f)));
};

export type EvolutionLine = { key: string, title: string, unknown: boolean, thisYear: GuestMixFigures[], lastYear: GuestMixFigures[] };

// The lines of the table order: one per row of the table (a key with no series is flat at 0).
export const evolutionLines = (evolution: GuestMixEvolution, table: GuestMixTable): EvolutionLine[] => {
	const empty = () => evolution.days.map(() => ZERO);
	const byKey = new Map(evolution.series.map(s => [s.key, s]));
	return table.rows.map(n => ({ key: n.key, title: n.title, unknown: n.unknown,
		thisYear: byKey.get(n.key)?.thisYear ?? empty(), lastYear: byKey.get(n.key)?.lastYear ?? empty() }));
};

// Build-up view (decision 15): a tile for each of the first 8 rows (Unknown left out) with room nights today or at STLY.
export const BUILD_UP_TILES = 8;
export const tileLines = (lines: EvolutionLine[]): EvolutionLine[] =>
	lines.filter(l => !l.unknown && (l.thisYear[l.thisYear.length - 1]?.roomNights > 0 || l.lastYear[l.lastYear.length - 1]?.roomNights > 0)).slice(0, BUILD_UP_TILES);

// Share view (decision 20): the share of each of the first EVOLUTION_LINES rows on each day, and "Other (N)". The shares of
// one day add up to 1 (Unknown included). measure 'rev' shares the revenue of the switches; ADR is not a share.
export const shareLines = (lines: EvolutionLine[], measure: 'rn' | 'rev', scope: RevenueScope, vat: VatBasis, days: number) => {
	const value = (f: GuestMixFigures) => measure === 'rn' ? f.roomNights : (revenueOf(f, scope, vat) ?? 0);
	const totals = Array.from({ length: days }, (_, i) => lines.reduce((s, l) => s + value(l.thisYear[i]), 0));
	const share = (vals: number[]) => vals.map((v, i) => totals[i] === 0 ? null : v / totals[i]);
	const top = lines.slice(0, EVOLUTION_LINES), rest = lines.slice(EVOLUTION_LINES);
	return [...top.map(l => ({ id: l.key === '' ? '(unknown)' : l.key, label: l.title, values: share(l.thisYear.map(value)) })),
		...(rest.length === 0 ? [] : [{ id: 'other', label: `Other (${rest.length})`, role: 'compset' as const,
			values: share(Array.from({ length: days }, (_, i) => rest.reduce((s, l) => s + value(l.thisYear[i]), 0))) }])];
};

// The figures of one row against last year (brief 4.3): now, last year at this point, the change (no change for ADR or when
// last year's point is 0), and the last 7 days (needs at least 8 points; not for ADR).
export const focusFigures = (thisYear: (number | null)[], lastYear: (number | null)[], measure: EvolutionMeasure) => {
	const last = thisYear.length - 1;
	const now = thisYear[last] ?? null, before = lastYear[last] ?? null;
	const week = (line: (number | null)[]) => last < 7 || line[last] === null || line[last - 7] === null ? null : (line[last] as number) - (line[last - 7] as number);
	return {
		now, lastYear: before,
		change: measure === 'adr' || now === null || before === null || before === 0 ? null : (now - before) / before,
		week: measure === 'adr' ? null : week(thisYear),
		weekLastYear: measure === 'adr' ? null : week(lastYear),
	};
};

// The change inside the window (ROO2-271 D2-a, the M-12 rule of the BUILD-UP column of the Rate Plans report): the first and
// the last point with a value. The days after the last night of the period have no value (T17 of ROO2-267).
export const windowChange = (values: (number | null)[]): { first: number, last: number, firstIndex: number, index: number } | null => {
	const known = values.map((v, i) => ({ v, i })).filter((x): x is { v: number, i: number } => x.v !== null);
	return known.length < 2 ? null
		: { first: known[0].v, last: known[known.length - 1].v, firstIndex: known[0].i, index: known[known.length - 1].i };
};

// A build-up tile (ROO2-271 D2, canvas direction A): now = the figure on the books now (the label of RN and Revenue, the big
// number of ADR); gained = its change inside the window (the big number of RN and Revenue, the change line of ADR);
// lastYearGained = last year's change in the same days (this year's first and last day with a value; none when last year has
// no value on one of them); vs = the change against last year's change, in % when last year gained, else the difference
// (none for ADR).
export const tileFigures = (thisYear: (number | null)[], lastYear: (number | null)[], measure: EvolutionMeasure) => {
	const known = thisYear.filter((v): v is number => v !== null);
	const now = windowChange(thisYear);
	const gained = now === null ? null : now.last - now.first;
	const firstLy = now === null ? null : lastYear[now.firstIndex] ?? null;
	const lastLy = now === null ? null : lastYear[now.index] ?? null;
	const lastYearGained = firstLy === null || lastLy === null ? null : lastLy - firstLy;
	const vs = measure === 'adr' || gained === null || lastYearGained === null ? null
		: lastYearGained > 0 ? { value: (gained - lastYearGained) / lastYearGained, format: 'percent' as const }
			: { value: gained - lastYearGained, format: measure === 'rn' ? 'number' as const : 'money' as const };
	return { now: known.length === 0 ? null : known[known.length - 1], gained, lastYearGained, vs };
};
